import React, { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FetchResult } from "@apollo/client";
import "./DataSourceInstanceDeleteDatasourceData.scss";
import { CNC_TRIGGER_DELETE_DATASOURCE_DATA } from "../../../CustomerProvisioningApi";
import useDeletionOperationStatus from "./useDeletionOperationStatus";
import {
  AlertIndicator,
  alertTypes,
  CheckBox,
  CheckBoxVariant,
  DateInput,
  getFormattedDate,
  Label,
  LabelVariant,
  ModalButtonsContainer,
  ModalContext,
  showErrorDialog,
  TextButton,
  TextInput,
  useMutationWrapper,
} from "@SeemplicityDev/shared";

const CONFIRM_PHRASE = "delete-datasource-data";

const OVERALL_STATUS = {
  IN_PROGRESS: "in_progress",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

interface StatusLabelEntry {
  text: string;
  variant: LabelVariant;
}

const STATUS_LABEL: Record<string, StatusLabelEntry> = {
  [OVERALL_STATUS.IN_PROGRESS]: {
    text: "In progress",
    variant: LabelVariant.OutlinedWarning,
  },
  [OVERALL_STATUS.SUCCEEDED]: {
    text: "Succeeded",
    variant: LabelVariant.Filled,
  },
  [OVERALL_STATUS.FAILED]: {
    text: "Failed",
    variant: LabelVariant.OutlinedError,
  },
};

interface DataSourceInstanceDeleteDatasourceDataProps {
  accountUuid: string;
  dsInstanceID: string;
  dsInstanceName: string;
}

interface DeleteDatasourceDataFormData {
  deleteResources: boolean;
  referenceDate: string;
  confirmAccountUuid: string;
  confirmDatasourceId: string;
  confirmPhrase: string;
}

interface TriggerDeleteDatasourceDataResult {
  cnc_trigger_delete_datasource_data: {
    op_id: string | null;
  } | null;
}

const DataSourceInstanceDeleteDatasourceData: React.FC<
  DataSourceInstanceDeleteDatasourceDataProps
> = ({ accountUuid, dsInstanceID, dsInstanceName }) => {
  const { closeModal } = useContext(ModalContext);
  const { t } = useTranslation();

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<DeleteDatasourceDataFormData>({
    mode: "all",
    defaultValues: {
      deleteResources: true,
      referenceDate: "",
      confirmAccountUuid: "",
      confirmDatasourceId: "",
      confirmPhrase: "",
    },
  });

  const deleteResources = watch("deleteResources");
  const referenceDate = watch("referenceDate");
  const confirmAccountUuid = watch("confirmAccountUuid");
  const confirmDatasourceId = watch("confirmDatasourceId");
  const confirmPhrase = watch("confirmPhrase");

  const [opId, setOpId] = useState<string | null>(null);
  const [isDryRunSubmitting, setIsDryRunSubmitting] = useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  const [triggerDeleteDatasourceData] =
    useMutationWrapper<TriggerDeleteDatasourceDataResult>(
      CNC_TRIGGER_DELETE_DATASOURCE_DATA
    );

  const { overallStatus, events, loading, error } = useDeletionOperationStatus({
    accountUuid,
    opId,
  });

  const isPolling = !!opId && overallStatus === OVERALL_STATUS.IN_PROGRESS;
  const isFinished =
    overallStatus === OVERALL_STATUS.SUCCEEDED ||
    overallStatus === OVERALL_STATUS.FAILED;

  // Account + datasource are restated blind (validated server-side); the phrase is a
  // known constant, so require an exact client-side match to gate the delete button.
  const confirmFieldsFilled =
    !!confirmAccountUuid?.trim() &&
    !!confirmDatasourceId?.trim() &&
    confirmPhrase === CONFIRM_PHRASE;

  const captureOpId = (res: FetchResult<TriggerDeleteDatasourceDataResult>) => {
    setOpId(res?.data?.cnc_trigger_delete_datasource_data?.op_id ?? null);
  };

  // Empty string means "no watermark → delete everything" (server-side default).
  const referenceDateOrNull = referenceDate?.trim() ? referenceDate : null;

  const runDryRun = () => {
    setIsDryRunSubmitting(true);
    triggerDeleteDatasourceData({
      variables: {
        account_uuid: accountUuid,
        datasource_id: dsInstanceID,
        dry_run: true,
        delete_resources: deleteResources,
        reference_date: referenceDateOrNull,
      },
    })
      .then(captureOpId)
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      })
      .finally(() => {
        setIsDryRunSubmitting(false);
      });
  };

  const deleteDatasourceData = () => {
    setIsDeleteSubmitting(true);
    triggerDeleteDatasourceData({
      variables: {
        account_uuid: accountUuid,
        datasource_id: dsInstanceID,
        dry_run: false,
        delete_resources: deleteResources,
        reference_date: referenceDateOrNull,
        confirm_account_uuid: confirmAccountUuid,
        confirm_datasource_id: confirmDatasourceId,
        confirm_phrase: confirmPhrase,
      },
    })
      .then(captureOpId)
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      })
      .finally(() => {
        setIsDeleteSubmitting(false);
      });
  };

  const statusLabel = overallStatus ? STATUS_LABEL[overallStatus] : null;

  return (
    <div className={"delete-datasource-data"}>
      {/* Preview (dry run) — safe, no confirmation, separated from the destructive action below */}
      <section className={"delete-datasource-data-section"}>
        <div className={"delete-datasource-data-section-title"}>
          {t("Preview (dry run)")}
        </div>
        <p className={"delete-datasource-data-hint"}>
          {t(
            "Runs the full pipeline and logs the counts it would delete — without deleting anything."
          )}
        </p>

        <Controller
          name={"deleteResources"}
          control={control}
          render={({ field: { value, onChange } }) => (
            <CheckBox
              variant={CheckBoxVariant.TOGGLE}
              checked={value}
              data-testid={"delete-resources-toggle"}
              label={t(
                "Also delete resources (applies to both dry run and delete)"
              )}
              onChange={(_val, isChecked) => onChange(isChecked)}
            />
          )}
        />

        <Controller
          name={"referenceDate"}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <DateInput
              value={value}
              data-testid={"reference-date"}
              labelTop
              name={"referenceDate"}
              onBlur={onBlur}
              onChange={onChange}
              maxDate={new Date().toISOString()}
              label={t("Reference date (optional)")}
              withHelp={t(
                "Only used to slice the deletion if the datasource is too big to delete in one run. The pipeline will only touch items last-updated on or before this date. Leave empty to delete everything (default). Applies to both dry run and delete."
              )}
            />
          )}
        />

        <div className={"delete-datasource-data-actions"}>
          <TextButton
            type={"button"}
            label={t("Run dry-run")}
            data-testid={"run-dry-run"}
            buttonStyle={"secondary"}
            rightIcon={isDryRunSubmitting ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isDryRunSubmitting}
            disabled={isDryRunSubmitting || isDeleteSubmitting}
            onClick={runDryRun}
          />
        </div>
      </section>

      <div className={"delete-datasource-data-divider"} />

      {/* Permanent delete — destructive; gated on restating account + datasource + phrase */}
      <section className={"delete-datasource-data-section"}>
        <div className={"delete-datasource-data-section-title"}>
          {t("Permanent delete")}
        </div>

        <AlertIndicator
          type={alertTypes.WARNING}
          wrapperClassName={"connection-alert"}
          title={t("This action cannot be undone")}
          content={
            <span className={"delete-datasource-data-warning"}>
              {t(
                "This will permanently delete all findings and aggregation groups collected by the datasource instance {{name}}.",
                { name: dsInstanceName }
              )}
              {/* Always rendered so toggling "delete resources" reserves this line's
                  space and never resizes the modal; only its visibility flips. */}
              <span
                className={`delete-datasource-data-warning-resources${
                  deleteResources ? "" : " is-hidden"
                }`}
              >
                {t("Resources unique to this datasource will also be deleted.")}
              </span>
            </span>
          }
        />

        <div className={"delete-datasource-data-confirm"}>
          <Controller
            name={"confirmAccountUuid"}
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"confirm-account-uuid"}
                labelTop
                required
                name={"confirmAccountUuid"}
                onBlur={onBlur}
                onChange={onChange}
                label={t("Confirm account UUID")}
              />
            )}
          />

          <Controller
            name={"confirmDatasourceId"}
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"confirm-datasource-id"}
                labelTop
                required
                name={"confirmDatasourceId"}
                onBlur={onBlur}
                onChange={onChange}
                label={t(
                  "Confirm datasource ID (Postgres datasources.datasource_id value)"
                )}
              />
            )}
          />

          <Controller
            name={"confirmPhrase"}
            control={control}
            rules={{
              // Only flag once something is typed; an empty field just leaves the
              // delete button disabled without showing an error.
              validate: (value) =>
                !value ||
                value === CONFIRM_PHRASE ||
                t('Confirmation phrase must be "{{phrase}}"', {
                  phrase: CONFIRM_PHRASE,
                }),
            }}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"confirm-phrase"}
                labelTop
                required
                name={"confirmPhrase"}
                onBlur={onBlur}
                onChange={onChange}
                error={errors.confirmPhrase ? errors.confirmPhrase : null}
                label={t('Confirmation phrase (type "{{phrase}}")', {
                  phrase: CONFIRM_PHRASE,
                })}
              />
            )}
          />
        </div>

        <div className={"delete-datasource-data-actions"}>
          <TextButton
            type={"button"}
            label={t("Delete datasource data")}
            data-testid={"delete-datasource-data"}
            buttonStyle={"danger"}
            rightIcon={isDeleteSubmitting ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isDeleteSubmitting}
            disabled={
              !confirmFieldsFilled || isDeleteSubmitting || isDryRunSubmitting
            }
            onClick={deleteDatasourceData}
          />
        </div>
      </section>

      {opId ? (
        <>
          <div className={"delete-datasource-data-divider"} />

          <div className={"delete-datasource-data-oplog"}>
            <div className={"delete-datasource-data-oplog-row"}>
              <span className={"delete-datasource-data-oplog-label"}>
                {t("Operation ID")}
              </span>
              <code data-testid={"deletion-op-id"}>{opId}</code>
            </div>
            <div className={"delete-datasource-data-oplog-hint"}>
              {t(
                "Tracked only while this window is open. To follow it in the logs, filter for"
              )}{" "}
              <code>op_id:{opId}</code>
            </div>
          </div>

          <div
            className={"delete-datasource-data-status"}
            data-testid={"deletion-status-panel"}
          >
            <div className={"delete-datasource-data-status-header"}>
              <span className={"delete-datasource-data-status-title"}>
                {t("Operation status")}
              </span>
              {statusLabel ? (
                <span
                  data-testid={"deletion-status-pill"}
                  className={
                    overallStatus === OVERALL_STATUS.SUCCEEDED
                      ? "deletion-status-pill-success"
                      : undefined
                  }
                >
                  <Label
                    text={t(statusLabel.text)}
                    variant={statusLabel.variant}
                  />
                </span>
              ) : null}
              {(isPolling || (loading && !isFinished)) && (
                <i
                  className="spinner seem-icon seem-icon-spinner"
                  data-testid={"deletion-status-spinner"}
                />
              )}
            </div>

            {error ? (
              <div className={"delete-datasource-data-status-error"}>
                {t("Failed to load operation status: {{message}}", {
                  message: error.message,
                })}
              </div>
            ) : null}

            {isPolling ? (
              <div className={"delete-datasource-data-status-hint"}>
                {t("This can take several minutes to complete.")}
              </div>
            ) : null}

            {events.length > 0 ? (
              <div className={"delete-datasource-data-events-header"}>
                <span>{t("Component")}</span>
                <span>{t("Step")}</span>
                <span>{t("Status")}</span>
                <span>{t("Count")}</span>
                <span>{t("Time")}</span>
              </div>
            ) : null}

            <ul className={"delete-datasource-data-events"}>
              {events.map((event, index) => (
                <li
                  className={"delete-datasource-data-event"}
                  key={`${event?.component}-${event?.step}-${event?.reference_id}-${index}`}
                  data-testid={"deletion-status-event"}
                >
                  <span className={"delete-datasource-data-event-component"}>
                    {event?.component}
                  </span>
                  <span className={"delete-datasource-data-event-step"}>
                    {event?.step}
                  </span>
                  <span className={"delete-datasource-data-event-status"}>
                    {event?.status}
                  </span>
                  <span className={"delete-datasource-data-event-count"}>
                    {event?.item_count != null ? event.item_count : "-"}
                  </span>
                  <span className={"delete-datasource-data-event-time"}>
                    {event?.inserted_at
                      ? getFormattedDate(event.inserted_at)
                      : "-"}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <ModalButtonsContainer>
            <TextButton
              type={"button"}
              label={t("Close")}
              data-testid={"close-deletion-modal"}
              buttonStyle={"secondary"}
              onClick={closeModal}
            />
          </ModalButtonsContainer>
        </>
      ) : null}
    </div>
  );
};

export default DataSourceInstanceDeleteDatasourceData;
